import { z } from 'zod';
import { MAX_SELECTOR_LEN, MAX_STACK_HEAD_LEN, type EventType } from './constants.js';

/**
 * §4.2 — olay tipi başına props sözleşmeleri.
 * Tüm şemalar `.passthrough()`: SDK örnekleme meta'sı (`_sr`, §19.1) ve ileri sürüm
 * alanları reddedilmeden geçer; bilinen alanlar tip güvenli kalır.
 */

export const utmSchema = z
  .object({
    source: z.string().max(128).optional(),
    medium: z.string().max(128).optional(),
    campaign: z.string().max(256).optional(),
    term: z.string().max(256).optional(),
    content: z.string().max(256).optional(),
  })
  .passthrough();

export type Utm = z.infer<typeof utmSchema>;

const pageViewProps = z
  .object({
    title: z.string().max(512).default(''),
    utm: utmSchema.optional(),
  })
  .passthrough();

const pageLeaveProps = z
  .object({
    duration_ms: z.number().nonnegative(),
    max_scroll_pct: z.number().min(0).max(100),
  })
  .passthrough();

const clickProps = z
  .object({
    selector: z.string().max(MAX_SELECTOR_LEN),
    /** metnin sha1 ilk 8 hex — PII sızdırmadan aynılık kıyası */
    text_hash: z.string().max(16),
    tag: z.string().max(32),
    x_pct: z.number().min(0).max(100).optional(),
    y_pct: z.number().min(0).max(100).optional(),
  })
  .passthrough();

const rageClickProps = z
  .object({
    selector: z.string().max(MAX_SELECTOR_LEN),
    count: z.number().int().min(3),
    window_ms: z.number().nonnegative(),
  })
  .passthrough();

const deadClickProps = z
  .object({
    selector: z.string().max(MAX_SELECTOR_LEN),
  })
  .passthrough();

const scrollDepthProps = z
  .object({
    pct: z.union([z.literal(25), z.literal(50), z.literal(75), z.literal(100)]),
  })
  .passthrough();

const formFocusProps = z
  .object({
    form_id: z.string().max(128),
    /** field = name/id, DEĞER ASLA (§9.5) */
    field: z.string().max(128),
  })
  .passthrough();

const formErrorProps = z
  .object({
    form_id: z.string().max(128),
    field: z.string().max(128),
    kind: z.string().max(64),
  })
  .passthrough();

const inputRetryProps = z
  .object({
    form_id: z.string().max(128),
    field: z.string().max(128),
    clear_count: z.number().int().min(2),
  })
  .passthrough();

const formSubmitProps = z
  .object({
    form_id: z.string().max(128),
    field_count: z.number().int().nonnegative(),
    duration_ms: z.number().nonnegative(),
  })
  .passthrough();

const jsErrorProps = z
  .object({
    message: z.string().max(1000),
    source: z.string().max(512).default(''),
    line: z.number().int().nonnegative().default(0),
    /** ilk 3 satır, maks 500 kr */
    stack_head: z.string().max(MAX_STACK_HEAD_LEN).default(''),
  })
  .passthrough();

const apiErrorProps = z
  .object({
    /** YALNIZCA path — query/body ASLA (§4.2) */
    endpoint_path: z.string().max(512),
    status: z.number().int().min(100).max(599),
    duration_ms: z.number().nonnegative(),
  })
  .passthrough();

const webVitalsProps = z
  .object({
    lcp_ms: z.number().nonnegative().optional(),
    cls: z.number().nonnegative().optional(),
    inp_ms: z.number().nonnegative().optional(),
    ttfb_ms: z.number().nonnegative().optional(),
  })
  .passthrough();

const customProps = z
  .object({
    name: z.string().max(128),
    data: z.record(z.unknown()).optional(),
  })
  .passthrough();

export const propsSchemaByType = {
  page_view: pageViewProps,
  page_leave: pageLeaveProps,
  click: clickProps,
  rage_click: rageClickProps,
  dead_click: deadClickProps,
  scroll_depth: scrollDepthProps,
  form_focus: formFocusProps,
  form_error: formErrorProps,
  input_retry: inputRetryProps,
  form_submit: formSubmitProps,
  js_error: jsErrorProps,
  api_error: apiErrorProps,
  web_vitals: webVitalsProps,
  custom: customProps,
} as const satisfies Record<EventType, z.ZodTypeAny>;

export type PropsByType = {
  [K in EventType]: z.infer<(typeof propsSchemaByType)[K]>;
};

/** §4.2 tip başına tam olay tipi — tip daraltma için. */
export type TypedEvent<K extends EventType = EventType> = {
  v: 1;
  pid: string;
  aid: string;
  sid: string;
  ts: number;
  seq: number;
  type: K;
  url: string;
  ref?: string;
  props: PropsByType[K];
};
